import React, { useState } from 'react';
import { CreditCardIcon, CheckCircleIcon, XCircleIcon, ShieldCheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function AdminPaymentTestCards() {
  const [copiedCard, setCopiedCard] = useState(null);

  const testCards = [
    {
      id: 'success',
      category: 'Başarılı Ödeme',
      categoryColor: 'green',
      icon: CheckCircleIcon,
      cards: [
        {
          name: 'MasterCard (Başarılı)',
          number: '5528 7900 0000 0008',
          cvv: '123',
          expiry: '12/30',
          threeDPassword: 'secret',
          description: '3D Secure ile başarılı ödeme',
          bank: 'Test Bank',
        },
        {
          name: 'Visa (Başarılı)',
          number: '4603 4500 0000 0000',
          cvv: '123',
          expiry: '12/30',
          threeDPassword: 'secret',
          description: '3D Secure ile başarılı ödeme',
          bank: 'Test Bank',
        }
      ]
    },
    {
      id: 'failed',
      category: 'Başarısız Ödeme',
      categoryColor: 'red',
      icon: XCircleIcon,
      cards: [
        {
          name: 'Yetersiz Bakiye',
          number: '5406 6700 0000 0009',
          cvv: '123',
          expiry: '12/30',
          threeDPassword: '',
          description: 'Yetersiz bakiye hatası simülasyonu',
          bank: 'Test Bank',
        },
        {
          name: 'Kart Limit Aşımı',
          number: '4355 0800 0000 0001',
          cvv: '123',
          expiry: '12/30',
          threeDPassword: '',
          description: 'Kart limit aşımı hatası',
          bank: 'Test Bank',
        }
      ]
    },
    {
      id: 'threeds',
      category: '3D Secure Test',
      categoryColor: 'blue',
      icon: ShieldCheckIcon,
      cards: [
        {
          name: '3D Secure Başarılı',
          number: '5528 7900 0000 0008',
          cvv: '123',
          expiry: '12/30',
          threeDPassword: 'secret',
          description: '3D Secure şifresi: secret',
          bank: 'Test Bank',
        },
        {
          name: '3D Secure Alternatif',
          number: '5528 7900 0000 0008',
          cvv: '123',
          expiry: '12/30',
          threeDPassword: '123456',
          description: '3D Secure şifresi: 123456',
          bank: 'Test Bank',
        }
      ]
    },
    {
      id: 'fraud',
      category: 'Fraud & Güvenlik',
      categoryColor: 'orange',
      icon: XCircleIcon,
      cards: [
        {
          name: 'Sahte Kart',
          number: '4355 0840 0000 0001',
          cvv: '123',
          expiry: '12/30',
          threeDPassword: '',
          description: 'Fraud engelleme testi',
          bank: 'Test Bank',
        }
      ]
    }
  ];

  const copyToClipboard = (text, cardName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedCard(cardName);
      toast.success(`${cardName} kopyalandı!`, {
        position: 'top-center',
        autoClose: 2000,
      });
      setTimeout(() => setCopiedCard(null), 2000);
    });
  };

  const copyCardInfo = (card) => {
    const info = `Kart: ${card.number}\nCVV: ${card.cvv}\nSon Kullanma: ${card.expiry}${card.threeDPassword ? `\n3D Şifre: ${card.threeDPassword}` : ''}`;
    navigator.clipboard.writeText(info).then(() => {
      toast.success('Kart bilgileri kopyalandı!', {
        position: 'top-center',
        autoClose: 2000,
      });
    });
  };

  const getCategoryBgColor = (color) => {
    const colors = {
      green: 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200',
      red: 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200',
      blue: 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200',
      orange: 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200'
    };
    return colors[color] || 'bg-gray-50';
  };

  const getCategoryTextColor = (color) => {
    const colors = {
      green: 'text-green-700',
      red: 'text-red-700',
      blue: 'text-blue-700',
      orange: 'text-orange-700'
    };
    return colors[color] || 'text-gray-700';
  };

  const getCategoryIconBg = (color) => {
    const colors = {
      green: 'bg-green-500',
      red: 'bg-red-500',
      blue: 'bg-blue-500',
      orange: 'bg-orange-500'
    };
    return colors[color] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-3xl mb-6 shadow-2xl">
            <CreditCardIcon className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">İyzico Test Kartları</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ödeme sistemini test etmek için kullanabileceğiniz İyzico Sandbox test kartları
          </p>
        </div>

        {/* Warning Banner */}
        <div className="bg-gradient-to-r from-amber-100 to-yellow-100 border-l-4 border-amber-500 p-6 rounded-2xl mb-8 shadow-md">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-bold text-amber-800 mb-1">⚠️ Önemli Uyarı</h3>
              <p className="text-amber-700">
                Bu kartlar yalnızca <strong>Sandbox/Test</strong> ortamında çalışır. Gerçek para işlemi yapılmaz.
                Production ortamında bu kartları kullanmayın.
              </p>
            </div>
          </div>
        </div>

        {/* Test Cards by Category */}
        <div className="space-y-8">
          {testCards.map((category) => {
            const Icon = category.icon;
            return (
              <div key={category.id} className={`${getCategoryBgColor(category.categoryColor)} border-2 rounded-3xl p-8 shadow-xl`}>
                {/* Category Header */}
                <div className="flex items-center mb-6">
                  <div className={`w-14 h-14 ${getCategoryIconBg(category.categoryColor)} rounded-2xl flex items-center justify-center shadow-lg mr-4`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className={`text-2xl font-bold ${getCategoryTextColor(category.categoryColor)}`}>
                      {category.category}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">{category.cards.length} test kartı</p>
                  </div>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.cards.map((card, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-gray-300 group"
                    >
                      {/* Card Header */}
                      <div className={`${getCategoryIconBg(category.categoryColor)} px-6 py-4`}>
                        <h3 className="text-white font-bold text-lg flex items-center justify-between">
                          <span>{card.name}</span>
                          <CreditCardIcon className="w-6 h-6" />
                        </h3>
                        <p className="text-white text-opacity-90 text-sm mt-1">{card.bank}</p>
                      </div>

                      {/* Card Body */}
                      <div className="p-6">
                        {/* Card Number */}
                        <div className="mb-4">
                          <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Kart Numarası</label>
                          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <span className="font-mono text-sm font-medium text-gray-800">{card.number}</span>
                            <button
                              onClick={() => copyToClipboard(card.number.replace(/\s/g, ''), card.name)}
                              className="text-gray-400 hover:text-blue-500 transition-colors"
                              title="Kopyala"
                            >
                              <ClipboardDocumentIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {/* CVV & Expiry */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">CVV</label>
                            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                              <span className="font-mono text-sm font-medium text-gray-800">{card.cvv}</span>
                              <button
                                onClick={() => copyToClipboard(card.cvv, 'CVV')}
                                className="text-gray-400 hover:text-blue-500 transition-colors"
                              >
                                <ClipboardDocumentIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Son Kullanma</label>
                            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                              <span className="font-mono text-sm font-medium text-gray-800">{card.expiry}</span>
                              <button
                                onClick={() => copyToClipboard(card.expiry, 'Tarih')}
                                className="text-gray-400 hover:text-blue-500 transition-colors"
                              >
                                <ClipboardDocumentIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* 3D Secure Password */}
                        {card.threeDPassword && (
                          <div className="mb-4">
                            <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">3D Secure Şifre</label>
                            <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg border border-blue-200">
                              <span className="font-mono text-sm font-bold text-blue-700">{card.threeDPassword}</span>
                              <button
                                onClick={() => copyToClipboard(card.threeDPassword, '3D Şifre')}
                                className="text-blue-400 hover:text-blue-600 transition-colors"
                              >
                                <ClipboardDocumentIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Description */}
                        <p className="text-sm text-gray-600 mb-4 border-t border-gray-100 pt-4">
                          {card.description}
                        </p>

                        {/* Copy All Button */}
                        <button
                          onClick={() => copyCardInfo(card)}
                          className={`w-full ${getCategoryIconBg(category.categoryColor)} text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl`}
                        >
                          <ClipboardDocumentIcon className="w-5 h-5" />
                          <span>Tüm Bilgileri Kopyala</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-12 bg-white rounded-3xl shadow-xl p-8 border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <svg className="w-7 h-7 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Kullanım Bilgileri
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100">
              <h4 className="font-bold text-blue-800 mb-3 flex items-center">
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                Test Ortamı
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Bu kartlar sadece <strong>Sandbox</strong> ortamında geçerlidir</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Gerçek para işlemi yapılmaz</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Test sonuçları anında görülebilir</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-100">
              <h4 className="font-bold text-purple-800 mb-3 flex items-center">
                <ShieldCheckIcon className="w-5 h-5 mr-2" />
                3D Secure Test
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>3D Secure ekranı açılacaktır</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>Test şifresi: <code className="bg-purple-100 px-2 py-0.5 rounded font-mono">secret</code> veya <code className="bg-purple-100 px-2 py-0.5 rounded font-mono">123456</code></span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>Doğru şifre ile işlem onaylanır</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Documentation Link */}
        <div className="mt-8 text-center">
          <a
            href="https://dev.iyzipay.com/tr/test-kartlari"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors"
          >
            <span>İyzico Resmi Dokümantasyonu</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}

export default AdminPaymentTestCards;
